import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  ProgressStage,
  OverallProgress,
  ProgressConfiguration,
} from './multi-stage-progress.types';

export type ImportType = 'image' | 'video' | 'document';

@Injectable({
  providedIn: 'root',
})
export class MultiStageProgressService {
  private readonly DEFAULT_CONFIGURATIONS: Record<
    ImportType,
    ProgressConfiguration
  > = {
    image: {
      stages: {
        export: { start: 0, end: 50 },
        download: { start: 0, end: 50 },
        upload: { start: 50, end: 100 },
        encoding: { start: 50, end: 100 },
      },
    },
    video: {
      stages: {
        export: { start: 0, end: 20 },
        download: { start: 0, end: 20 },
        upload: { start: 20, end: 60 },
        encoding: { start: 60, end: 100 },
      },
    },
    document: {
      // Documents (e.g. PDFs) don't go through file download/upload stages.
      stages: {
        export: { start: 0, end: 70 },
        encoding: { start: 70, end: 100 },
      },
    },
  };

  private overallProgress: number = 0;
  private currentStage: ProgressStage = 'export';
  private importType: ImportType | null = null;
  private config: ProgressConfiguration | null = null;
  private progressSubject: BehaviorSubject<{
    overall: number;
    stage: ProgressStage;
  }>;

  constructor() {
    this.progressSubject = new BehaviorSubject<{
      overall: number;
      stage: ProgressStage;
    }>({
      overall: 0,
      stage: 'export',
    });
  }

  get progress$(): Observable<{ overall: number; stage: ProgressStage }> {
    return this.progressSubject.asObservable();
  }

  start(type: ImportType): void {
    this.importType = type;
    this.config = this.DEFAULT_CONFIGURATIONS[type];
    this.overallProgress = 0;
    this.currentStage = 'export';
    this.emitProgress();
  }

  updateStageProgress(stage: ProgressStage, progress: number): void {
    if (!this.config) {
      throw new Error('Progress tracker not initialized. Call start() first.');
    }

    const overall = this.mapStageToOverall(stage, progress, this.config);
    this.overallProgress = overall.overall;
    this.currentStage = overall.stage;
    this.emitProgress();
  }

  updateFileProgress(fileProgress: number): void {
    if (!this.config || !this.importType) {
      throw new Error('Progress tracker not initialized. Call start() first.');
    }

    let stage: ProgressStage;
    let stageProgress: number;

    if (this.importType === 'video') {
      if (fileProgress < 50) {
        stage = 'upload';
        stageProgress = fileProgress * 2;
      } else if (fileProgress === 50 && this.currentStage === 'upload') {
        stage = 'encoding';
        stageProgress = 0;
      } else {
        stage = 'encoding';
        stageProgress = (fileProgress - 50) * 2;
      }
    } else if (this.importType === 'image') {
      stage = 'upload';
      stageProgress = fileProgress;
    } else {
      // Documents don't have file-upload progress.
      return;
    }

    this.updateStageProgress(stage, stageProgress);
  }

  getOverallProgress(): number {
    return this.overallProgress;
  }

  getCurrentStage(): ProgressStage {
    return this.currentStage;
  }

  isEncodingActive(): boolean {
    return (
      this.currentStage === 'encoding' ||
      (this.importType === 'video' && this.currentStage === 'upload')
    );
  }

  reset(): void {
    this.overallProgress = 0;
    this.currentStage = 'export';
    this.importType = null;
    this.config = null;
    this.emitProgress();
  }

  private emitProgress(): void {
    this.progressSubject.next({
      overall: this.overallProgress,
      stage: this.currentStage,
    });
  }

  mapStageToOverall(
    stage: ProgressStage,
    stageProgress: number,
    config: ProgressConfiguration,
  ): OverallProgress {
    const stageRange = config.stages[stage];

    if (!stageRange) {
      throw new Error(`Invalid stage: ${stage} for configuration`);
    }

    const rangeSize = stageRange.end - stageRange.start;
    const overall = stageRange.start + (stageProgress / 100) * rangeSize;

    return {
      overall: Math.min(100, Math.max(0, overall)),
      stage,
    };
  }

  createConfiguration(type: ImportType): ProgressConfiguration {
    return this.DEFAULT_CONFIGURATIONS[type];
  }
}
